import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Divider, Slider, TextField, Button,
  Select, MenuItem, FormControl, InputLabel, Alert, Paper, Chip, IconButton,
  Collapse, Tabs, Tab, LinearProgress, Tooltip, ToggleButton, ToggleButtonGroup,
  Link, useMediaQuery, useTheme } from '@mui/material';
import { Save, Send, ArrowBack, NavigateBefore, NavigateNext, SkipNext,
  ChatBubbleOutline, InfoOutlined, OpenInNew } from '@mui/icons-material';
import { reviewerAPI } from '../../services/api';
import { useSnackbar } from '../../utils/notify';
import dayjs from 'dayjs';
import { finalScoreFor } from '../../utils/formula';

var AUTOSAVE_DELAY_MS = 1500;

function scoreOf(scores, id) {
  var s = scores[id];
  if (!s) return null;
  return (typeof s.score === 'number') ? s.score : null;
}

function commentOf(scores, id) {
  var s = scores[id];
  return (s && s.comment) || '';
}

function answerOf(scores, id) {
  var s = scores[id];
  return (s && s.answer) || '';
}

// Types that produce a number and feed the weighted total. Everything else is
// a written response: captured, shown to admins, but not arithmetic.
function isNumeric(type) {
  return ['slider', 'radio', 'number', 'select'].indexOf(type || 'slider') >= 0;
}

// "Done" means whatever completion means for this type.
function isAnswered(scores, c) {
  if (isNumeric(c.scoring_type)) return scoreOf(scores, c.id) !== null;
  return answerOf(scores, c.id).trim() !== '';
}

function serializeScores(scores) {
  var out = {};
  Object.keys(scores).forEach(function(id) {
    var s = scores[id] || {};
    var hasScore = typeof s.score === 'number';
    var hasComment = s.comment && s.comment.length > 0;
    var hasAnswer = s.answer && s.answer.length > 0;
    if (!hasScore && !hasComment && !hasAnswer) return;
    out[id] = {};
    if (hasScore) out[id].score = s.score;
    if (hasComment) out[id].comment = s.comment;
    if (hasAnswer) out[id].answer = s.answer;
  });
  return out;
}

function isBlank(v) {
  if (v === null || v === undefined) return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'string') return v.trim() === '';
  return false;
}

// Group visible fields by section. restrictSectionIds narrows it to one stage's
// sections; hideEmpty drops anything the applicant left blank, which is what
// makes a multi-pathway form readable and what lets a stage be skipped whole.
function buildGroups(schema, visibleFieldIds, visibleSectionIds, restrictSectionIds, responses, hideEmpty) {
  var allFields = ((schema && schema.fields) || []).slice()
    .sort(function(a, b) { return (a.order || 0) - (b.order || 0); });
  var allSections = ((schema && schema.sections) || []).slice()
    .sort(function(a, b) { return (a.order || 0) - (b.order || 0); });

  var fieldIds = visibleFieldIds || [];
  var sectionIds = visibleSectionIds || [];
  var restrict = restrictSectionIds || [];

  var fields = fieldIds.length > 0
    ? allFields.filter(function(f) { return fieldIds.indexOf(f.id) >= 0; })
    : allFields;

  var sections = sectionIds.length > 0
    ? allSections.filter(function(s) { return sectionIds.indexOf(s.id) >= 0; })
    : allSections;

  if (sectionIds.length > 0) {
    fields = fields.filter(function(f) { return f.section_id && sectionIds.indexOf(f.section_id) >= 0; });
  }

  if (restrict.length > 0) {
    sections = sections.filter(function(s) { return restrict.indexOf(s.id) >= 0; });
    fields = fields.filter(function(f) { return f.section_id && restrict.indexOf(f.section_id) >= 0; });
  }

  var hiddenCount = 0;
  if (hideEmpty) {
    var before = fields.length;
    fields = fields.filter(function(f) {
      if (f.type === 'file_upload') return true;
      return !isBlank(responses[f.id]);
    });
    hiddenCount = before - fields.length;
  }

  var groups = [];
  var loose = fields.filter(function(f) { return !f.section_id; });
  if (loose.length) groups.push({ id: '__none__', title: '', fields: loose });

  sections.forEach(function(sec) {
    var sf = fields.filter(function(f) { return f.section_id === sec.id; });
    if (sf.length) groups.push({ id: sec.id, title: sec.title, fields: sf });
  });

  var placed = {};
  groups.forEach(function(g) { g.fields.forEach(function(f) { placed[f.id] = true; }); });
  var orphans = fields.filter(function(f) { return !placed[f.id]; });
  if (orphans.length) groups.push({ id: '__other__', title: 'Other', fields: orphans });

  var fieldCount = 0;
  groups.forEach(function(g) { fieldCount += g.fields.length; });

  return { groups: groups, hiddenCount: hiddenCount, fieldCount: fieldCount };
}

// Turn the configured stages into what this particular application needs:
// stages whose sections are entirely blank for this applicant drop out, and
// their criteria stop being required.
function buildStages(data, schema, responses, hideEmpty, criteria) {
  var configured = data.review_stages || [];
  if (typeof configured === 'string') { try { configured = JSON.parse(configured); } catch (e) { configured = []; } }
  var useStages = !!data.use_review_stages && configured.length > 0;

  if (!useStages) {
    var built = buildGroups(schema, data.visible_field_ids, data.visible_section_ids, null, responses, hideEmpty);
    return {
      staged: false,
      stages: [{ id: '__all__', label: 'Application', built: built, criteria: criteria }],
    };
  }

  // Criteria not placed in any stage would otherwise be unscorable; they ride
  // along with the last stage rather than disappearing.
  var assigned = {};
  configured.forEach(function(st) {
    (st.criteria_ids || []).forEach(function(cid) { assigned[cid] = true; });
  });
  var unassigned = criteria.filter(function(c) { return !assigned[c.id]; });

  var out = [];
  configured.forEach(function(st, i) {
    var built = buildGroups(schema, data.visible_field_ids, data.visible_section_ids,
      st.section_ids || [], responses, hideEmpty);
    var stageCriteria = criteria.filter(function(c) {
      return (st.criteria_ids || []).indexOf(c.id) >= 0;
    });
    if (i === configured.length - 1) stageCriteria = stageCriteria.concat(unassigned);

    // A stage with nothing to read for this applicant is not applicable to them.
    if (hideEmpty && built.fieldCount === 0) return;

    out.push({
      id: st.id || ('stage-' + i),
      label: st.label || ('Stage ' + (i + 1)),
      link_label: st.link_label || '',
      link_url: st.link_url || '',
      built: built,
      criteria: stageCriteria,
    });
  });

  if (out.length === 0) {
    var all = buildGroups(schema, data.visible_field_ids, data.visible_section_ids, null, responses, hideEmpty);
    return { staged: false, stages: [{ id: '__all__', label: 'Application', built: all, criteria: criteria }] };
  }

  return { staged: true, stages: out };
}

// Parse "Label|3" option lines into {label, score}.
function parseOption(line) {
  var parts = String(line).split('|');
  var label = parts[0].trim();
  var score = parts.length > 1 ? parseFloat(parts[1]) : NaN;
  return { label: label, score: isNaN(score) ? null : score };
}

function CriterionRow(props) {
  var E = React.createElement;
  var c = props.criterion;
  var type = c.scoring_type || 'slider';
  var numeric = isNumeric(type);
  var scoreVal = props.score;
  var answerVal = props.answer || '';
  var unscored = numeric ? scoreVal === null : answerVal.trim() === '';
  var optional = c.required === false;
  var os = useState(!!props.comment); var open = os[0], setOpen = os[1];
  var useButtons = (c.max_score || 0) <= 10;

  function statusText() {
    if (!numeric) return unscored ? (optional ? 'optional' : 'no answer yet') : 'answered';
    return unscored ? (optional ? 'optional' : 'not scored') : scoreVal + '/' + c.max_score;
  }

  function control() {
    if (type === 'text') {
      return E(TextField, {fullWidth:true, size:'small', value:answerVal,
        placeholder:c.placeholder || 'Your answer',
        onChange:function(e){ props.onAnswer(e.target.value); }, disabled:props.disabled});
    }
    if (type === 'textarea') {
      return E(TextField, {fullWidth:true, size:'small', multiline:true, rows:3, value:answerVal,
        placeholder:c.placeholder || 'Your answer',
        onChange:function(e){ props.onAnswer(e.target.value); }, disabled:props.disabled});
    }
    if (type === 'yes_no') {
      return E(ToggleButtonGroup, {size:'small', exclusive:true, fullWidth:true,
        value: answerVal || null, disabled:props.disabled,
        onChange:function(_, v){ if (v !== null) props.onAnswer(v); }},
        E(ToggleButton, {value:'Yes', sx:{py:0.25, fontSize:12}}, 'Yes'),
        E(ToggleButton, {value:'No', sx:{py:0.25, fontSize:12}}, 'No'));
    }
    if (type === 'number') {
      return E(TextField, {fullWidth:true, size:'small', type:'number',
        value: scoreVal === null ? '' : scoreVal,
        inputProps:{min:0, max:c.max_score || undefined},
        onChange:function(e){
          var v = e.target.value;
          if (v === '') { props.onClear(); return; }
          props.onScore(parseFloat(v));
        }, disabled:props.disabled});
    }
    if (type === 'select') {
      var opts = (c.options || []).map(parseOption);
      return E(FormControl, {fullWidth:true, size:'small'},
        E(Select, {value:answerVal, displayEmpty:true, disabled:props.disabled,
          onChange:function(e){
            var picked = opts.filter(function(o){ return o.label === e.target.value; })[0];
            props.onAnswer(e.target.value);
            if (picked && picked.score !== null) props.onScore(picked.score);
          }},
          E(MenuItem, {value:'', disabled:true}, E('em', null, 'Choose\u2026')),
          opts.map(function(o, k) {
            return E(MenuItem, {key:k, value:o.label},
              o.label + (o.score !== null ? '  (' + o.score + ')' : ''));
          })));
    }
    if (type === 'radio' || useButtons) {
      return E(ToggleButtonGroup, {size:'small', exclusive:true, fullWidth:true,
        value: scoreVal === null ? null : scoreVal, disabled:props.disabled,
        onChange:function(_, v){ if (v !== null) props.onScore(v); }},
        Array.from({length:(c.max_score || 0) + 1}, function(_, n){ return n; }).map(function(v) {
          return E(ToggleButton, {key:v, value:v, sx:{py:0.25, px:0, minWidth:0, fontSize:12}}, String(v));
        }));
    }
    return E(Slider, {value: scoreVal === null ? 0 : scoreVal, min:0, max:c.max_score || 0, step:1,
      onChange:function(_, v){ props.onScore(v); }, disabled:props.disabled,
      valueLabelDisplay:'auto', sx:{opacity: scoreVal === null ? 0.45 : 1}});
  }

  return E(Paper, {variant:'outlined',
    sx:{p:1.25, mb:1, borderColor: (unscored && !optional) ? 'warning.light' : 'divider'}},
    E(Box, {sx:{display:'flex', justifyContent:'space-between', alignItems:'center', gap:1, mb:0.5}},
      E(Box, {sx:{display:'flex', alignItems:'center', gap:0.5, flex:1, minWidth:0}},
        E(Typography, {variant:'body2', fontWeight:600, noWrap:true}, c.name),
        c.description
          ? E(Tooltip, {title:c.description, arrow:true, placement:'top'},
              E(InfoOutlined, {sx:{fontSize:15, color:'text.secondary', cursor:'help', flexShrink:0}}))
          : null,
        c.share_with_applicant
          ? E(Tooltip, {title:'This answer is intended to be shared with the applicant', arrow:true},
              E(Chip, {size:'small', label:'shared', variant:'outlined',
                sx:{height:16, fontSize:10, flexShrink:0}}))
          : null),
      E(Typography, {variant:'caption',
        color: (unscored && !optional) ? 'warning.main' : 'text.secondary'}, statusText()),
      E(IconButton, {size:'small', onClick:function(){ setOpen(!open); },
        color: props.comment ? 'primary' : 'default'},
        E(ChatBubbleOutline, {fontSize:'inherit'}))),

    control(),

    E(Collapse, {in:open},
      E(TextField, {fullWidth:true, size:'small', multiline:true, rows:2,
        placeholder:'Note on this criterion (optional)', value:props.comment,
        onChange:function(e){ props.onComment(e.target.value); },
        disabled:props.disabled, sx:{mt:1}})),

    (numeric && scoreVal !== null && !props.disabled)
      ? E(Button, {size:'small', sx:{mt:0.25, minWidth:0, p:0.25, fontSize:11}, onClick:props.onClear}, 'clear')
      : null);
}

export default function ReviewApplication() {
  var params = useParams(); var assignmentId = params.assignmentId;
  var navigate = useNavigate();
  var sn = useSnackbar(); var notify = sn.enqueueSnackbar;
  var theme = useTheme();
  var isMobile = useMediaQuery(theme.breakpoints.down('md'));

  var ds = useState(null); var data = ds[0]; var setData = ds[1];
  var ss = useState({}); var scores = ss[0]; var setScores = ss[1];
  var cs = useState(''); var comment = cs[0]; var setComment = cs[1];
  var rs = useState(''); var recommendation = rs[0]; var setRecommendation = rs[1];
  // Keyed by stage id. A single recommendation across several stages loses
  // whichever one was set first, which is exactly what used to happen.
  var sr = useState({}); var stageRecs = sr[0]; var setStageRecs = sr[1];
  var sb = useState(false); var submitting = sb[0]; var setSubmitting = sb[1];
  var sv = useState(false); var saving = sv[0]; var setSaving = sv[1];
  var dt = useState(false); var dirty = dt[0]; var setDirty = dt[1];
  var lu = useState(null); var savedAt = lu[0]; var setSavedAt = lu[1];
  var sib = useState([]); var siblings = sib[0]; var setSiblings = sib[1];
  var mt = useState('application'); var mobileTab = mt[0]; var setMobileTab = mt[1];
  var si = useState(0); var stageIdx = si[0]; var setStageIdx = si[1];

  var hydrated = useRef(false);
  var timer = useRef(null);
  var stateRef = useRef({ scores: {}, comment: '', recommendation: '', open: true });

  stateRef.current = {
    scores: scores,
    comment: comment,
    recommendation: recommendation,
    stageRecs: stageRecs,
    dirty: dirty,
    open: !!(data && data.review_open !== false),
  };

  useEffect(function() {
    hydrated.current = false;
    setDirty(false);
    setSubmitting(false);
    setSavedAt(null);
    setMobileTab('application');
    setStageIdx(0);
    reviewerAPI.getAssignment(assignmentId).then(function(r) {
      var d = r.data.data;
      var rv = d.review;
      var sc = {};
      if (rv) {
        sc = typeof rv.scores === 'string' ? JSON.parse(rv.scores) : (rv.scores || {});
        setComment(rv.overall_comment || '');
        setRecommendation(rv.recommendation || '');
        var sRec = rv.stage_recommendations;
        if (typeof sRec === 'string') { try { sRec = JSON.parse(sRec); } catch (e4) { sRec = {}; } }
        setStageRecs(sRec || {});
      } else {
        setComment('');
        setRecommendation('');
        setStageRecs({});
      }
      setScores(sc || {});
      setData(d);
      setTimeout(function() { hydrated.current = true; }, 0);
    }).catch(function(e) {
      var msg = (e.response && e.response.data && e.response.data.error) || 'Could not open this application';
      notify(msg, {variant:'error'});
      navigate('/reviewer');
    });
  }, [assignmentId]);

  useEffect(function() {
    reviewerAPI.myAssignments()
      .then(function(r) { setSiblings(r.data.data || []); })
      .catch(function() { setSiblings([]); });
  }, []);

  function persistDraft() {
    var st = stateRef.current;
    if (!st.open) return Promise.resolve();
    setSaving(true);
    return reviewerAPI.saveDraft(assignmentId, {
      scores: serializeScores(st.scores),
      overall_comment: st.comment,
      recommendation: st.recommendation,
      stage_recommendations: st.stageRecs || {},
    }).then(function() {
      setSaving(false); setDirty(false); setSavedAt(new Date());
    }).catch(function() {
      setSaving(false);
      notify('Could not save your draft. Your answers are still on screen \u2014 check your connection.', {variant:'error'});
    });
  }

  useEffect(function() {
    if (!hydrated.current) return undefined;
    if (!stateRef.current.open) return undefined;
    setDirty(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(persistDraft, AUTOSAVE_DELAY_MS);
    return function() { if (timer.current) clearTimeout(timer.current); };
  }, [scores, comment, recommendation, stageRecs]);

  useEffect(function() {
    function warn(e) {
      if (!stateRef.current.open) return;
      e.preventDefault();
      e.returnValue = '';
    }
    if (dirty) window.addEventListener('beforeunload', warn);
    return function() { window.removeEventListener('beforeunload', warn); };
  }, [dirty]);

  useEffect(function() {
    return function() {
      if (timer.current) clearTimeout(timer.current);
      if (hydrated.current && stateRef.current.open && stateRef.current.dirty) persistDraft();
    };
  }, [assignmentId]);

  function setScore(criterionId, value) {
    setScores(function(prev) {
      var next = Object.assign({}, prev);
      next[criterionId] = Object.assign({}, next[criterionId] || {}, {score: value});
      return next;
    });
  }
  function clearScore(criterionId) {
    setScores(function(prev) {
      var next = Object.assign({}, prev);
      var entry = Object.assign({}, next[criterionId] || {});
      delete entry.score;
      next[criterionId] = entry;
      return next;
    });
  }
  function setAnswer(criterionId, value) {
    setScores(function(prev) {
      var next = Object.assign({}, prev);
      next[criterionId] = Object.assign({}, next[criterionId] || {}, {answer: value});
      return next;
    });
  }

  function setStageRec(stageId, value) {
    setStageRecs(function(prev) {
      var next = Object.assign({}, prev);
      next[stageId] = value;
      return next;
    });
  }

  function setScoreComment(criterionId, value) {
    setScores(function(prev) {
      var next = Object.assign({}, prev);
      next[criterionId] = Object.assign({}, next[criterionId] || {}, {comment: value});
      return next;
    });
  }

  var E = React.createElement;
  if (!data) return null;

  var application = data.application;
  var criteria = data.criteria || [];
  var formTitle = data.form_title;
  var schema = typeof data.form_schema === 'string' ? JSON.parse(data.form_schema) : data.form_schema;
  if (typeof schema === 'string') { try { schema = JSON.parse(schema); } catch (e2) { schema = {}; } }
  var responses = typeof application.responses === 'string' ? JSON.parse(application.responses) : application.responses;
  responses = responses || {};
  var isCompleted = data.review && data.review.status === 'completed';
  var reviewOpen = data.review_open !== false;
  var anonymousReview = !!data.anonymous_review;
  var hideEmpty = data.hide_empty_for_reviewers !== false;
  var tierLabel = data.priority_tier || '';

  var stageInfo = buildStages(data, schema, responses, hideEmpty, criteria);
  var stages = stageInfo.stages;
  var activeIdx = Math.min(stageIdx, stages.length - 1);
  var stage = stages[activeIdx];

  // Required = every criterion in a stage this applicant actually has.
  var required = [];
  var seenReq = {};
  stages.forEach(function(s) {
    s.criteria.forEach(function(c) {
      if (c.required === false) return; // optional: shown, never blocks submit
      if (!seenReq[c.id]) { seenReq[c.id] = true; required.push(c); }
    });
  });

  function progressFor(list) {
    var done = 0;
    list.forEach(function(c) { if (isAnswered(scores, c)) done++; });
    return done;
  }

  var scoredCount = progressFor(required);

  // The backend recomputes and stores this on save; here it just keeps the
  // number in front of the reviewer as they work.
  var scored = finalScoreFor(criteria, function(id) { return scoreOf(scores, id); },
    data.score_formula, data.score_formula_enabled);
  var totalScore = scored.value;
  var usesFormula = !!(data.score_formula_enabled && data.score_formula && data.score_formula.trim());
  var maxTotal = 0;
  criteria.forEach(function(c) {
    if (!isNumeric(c.scoring_type)) return;
    maxTotal += (c.max_score || 0) * (c.weight || 1);
  });
  var allScored = required.length === 0 || scoredCount === required.length;

  var idx = -1;
  for (var i = 0; i < siblings.length; i++) {
    if (siblings[i].assignment_id === assignmentId) { idx = i; break; }
  }
  var prev = idx > 0 ? siblings[idx - 1] : null;
  var next = idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : null;
  var nextUnreviewed = null;
  for (var j = 0; j < siblings.length; j++) {
    var cand = siblings[(idx >= 0 ? idx + 1 + j : j) % (siblings.length || 1)];
    if (cand && cand.assignment_id !== assignmentId && cand.review_status !== 'completed') { nextUnreviewed = cand; break; }
  }

  function go(a) {
    if (!a) return;
    if (timer.current) clearTimeout(timer.current);
    var p = (hydrated.current && reviewOpen) ? persistDraft() : Promise.resolve();
    p.then(function() { navigate('/reviewer/review/' + a.assignment_id); });
  }

  function submitReview() {
    if (!allScored) {
      notify('Complete every required criterion before submitting \u2014 ' +
        (required.length - scoredCount) + ' still to go.', {variant:'warning'});
      return;
    }
    if (stageInfo.staged) {
      var missing = stages.filter(function(s2) { return !stageRecs[s2.id]; });
      if (missing.length > 0) {
        setStageIdx(stages.indexOf(missing[0]));
        notify('Choose a recommendation for ' + missing[0].label + ' before submitting.', {variant:'warning'});
        return;
      }
    } else if (!recommendation) {
      notify('Choose a recommendation before submitting.', {variant:'warning'});
      return;
    }
    setSubmitting(true);
    if (timer.current) clearTimeout(timer.current);
    reviewerAPI.submitReview(assignmentId, {
      scores: serializeScores(scores), overall_comment: comment, recommendation: recommendation,
      stage_recommendations: stageRecs,
    }).then(function() {
      setDirty(false);
      setSubmitting(false);
      hydrated.current = false;
      notify('Review submitted', {variant:'success'});
      if (nextUnreviewed) navigate('/reviewer/review/' + nextUnreviewed.assignment_id);
      else navigate('/reviewer');
    }).catch(function() {
      notify('Submit failed. Your answers are still on screen \u2014 try again.', {variant:'error'});
      setSubmitting(false);
    });
  }

  var displayName = anonymousReview
    ? 'Application #' + application.id.substring(0, 8).toUpperCase()
    : application.applicant_name;

  var saveLabel = saving ? 'Saving\u2026'
    : dirty ? 'Unsaved changes'
    : savedAt ? 'Saved ' + dayjs(savedAt).format('HH:mm')
    : '';

  function fieldBlock(field) {
    var val = responses[field.id];
    if (field.type === 'file_upload') {
      return E(Box, {key:field.id, sx:{mb:2, p:1.5, border:'1px solid', borderColor:'divider', borderRadius:1}},
        E(Typography, {variant:'subtitle2', color:'text.secondary', fontWeight:600}, field.label),
        E(Typography, {variant:'body2', fontStyle:'italic', color:'text.secondary'}, 'See uploaded files below'));
    }
    var displayVal = Array.isArray(val) ? val.join(', ') : (isBlank(val) ? '\u2014' : String(val));
    return E(Box, {key:field.id, sx:{mb:2, p:1.5, border:'1px solid', borderColor:'divider', borderRadius:1, minWidth:0}},
      E(Typography, {variant:'subtitle2', color:'text.secondary', fontWeight:600, sx:{wordBreak:'break-word'}}, field.label),
      field.type === 'textarea'
        ? E(Typography, {variant:'body1', sx:{whiteSpace:'pre-wrap', wordBreak:'break-word', overflowWrap:'anywhere'}}, displayVal)
        : E(Typography, {variant:'body1', sx:{wordBreak:'break-word', overflowWrap:'anywhere'}}, displayVal));
  }

  var applicationPanel = E(Card, null,
    E(CardContent, null,
      !anonymousReview ? E(Box, {sx:{mb:2, p:1.5, bgcolor:'grey.50', borderRadius:1}},
        E(Typography, {variant:'body2'}, 'Name: ', E('strong', null, application.applicant_name)),
        application.applicant_email ? E(Typography, {variant:'body2'}, 'Email: ', application.applicant_email) : null) : null,

      stage.built.groups.map(function(g) {
        return E(Box, {key:g.id, sx:{mb:3}},
          g.title ? E(Typography, {variant:'subtitle1', sx:{mb:1.5, fontWeight:700, color:'primary.main',
            borderBottom:1, borderColor:'divider', pb:0.5}}, g.title) : null,
          g.fields.map(fieldBlock));
      }),

      (hideEmpty && stage.built.hiddenCount > 0) ? E(Typography, {variant:'caption', color:'text.secondary',
        sx:{display:'block', mt:1, fontStyle:'italic'}},
        'Questions this applicant did not answer are hidden.') : null,

      (function() {
        try {
          var files = typeof application.files === 'string' ? JSON.parse(application.files) : application.files;
          if (files && files.length > 0) {
            return E(Box, {sx:{mt:2}},
              E(Typography, {variant:'subtitle2', color:'text.secondary', sx:{mb:1}}, 'Uploaded files'),
              files.map(function(f, k) {
                return E(Button, {key:k, size:'small', href:'/api/files/' + f.stored_name, target:'_blank',
                  sx:{display:'block', mb:0.5}}, f.filename);
              }));
          }
        } catch (e3) { /* files column is not valid JSON */ }
        return null;
      })()));

  var stageScored = progressFor(stage.criteria);
  var isLastStage = activeIdx === stages.length - 1;

  var scoringBody = E(Card, {sx:{flex: isMobile ? 'none' : 1, minHeight:0, display:'flex', flexDirection:'column'}},
    E(CardContent, {sx:{flex:1, minHeight:0, overflowY:'auto', p:2}},
      E(Box, {sx:{display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:1, mb:0.5}},
        E(Box, {sx:{minWidth:0}},
          E(Typography, {variant:'subtitle1', fontWeight:700, noWrap:true},
            stageInfo.staged ? stage.label : 'Scoring'),
          (stageInfo.staged && stage.link_url)
            ? E(Link, {href:stage.link_url, target:'_blank', rel:'noopener noreferrer', variant:'caption',
                sx:{display:'inline-flex', alignItems:'center', gap:0.25, mt:0.25}},
                stage.link_label || 'Reference', E(OpenInNew, {sx:{fontSize:12}}))
            : null),
        E(Tooltip, {title: scored.error ? 'Formula problem: ' + scored.error
            : usesFormula ? data.score_formula : ''},
          E(Chip, {size:'small',
            label: usesFormula
              ? 'Score ' + totalScore.toFixed(2)
              : totalScore.toFixed(1) + ' / ' + maxTotal.toFixed(1),
            color: scored.error ? 'warning' : allScored ? 'primary' : 'default'}))),
      required.length > 0 ? E(Box, {sx:{mb:1.5}},
        E(Typography, {variant:'caption', color: allScored ? 'success.main' : 'text.secondary'},
          stageInfo.staged
            ? stageScored + ' of ' + stage.criteria.length + ' here \u2022 ' + scoredCount + ' of ' + required.length + ' overall'
            : scoredCount + ' of ' + required.length + ' done'),
        E(LinearProgress, {variant:'determinate',
          value: required.length ? (scoredCount / required.length) * 100 : 0,
          sx:{height:5, borderRadius:3, mt:0.5}})) : null,

      stage.criteria.length === 0 ? E(Typography, {variant:'body2', color:'text.secondary', sx:{mb:2}},
        'Nothing to score here \u2014 read through, then move on.') : null,

      stage.criteria.map(function(c) {
        return E(CriterionRow, {key:c.id, criterion:c,
          score: scoreOf(scores, c.id), comment: commentOf(scores, c.id),
          answer: answerOf(scores, c.id),
          disabled: !reviewOpen,
          onScore: function(v){ setScore(c.id, v); },
          onClear: function(){ clearScore(c.id); },
          onAnswer: function(v){ setAnswer(c.id, v); },
          onComment: function(v){ setScoreComment(c.id, v); }});
      }),

      E(Divider, {sx:{my:1.5}}),

      E(FormControl, {fullWidth:true, size:'small', sx:{mb:1.5}},
        E(InputLabel, null, stageInfo.staged ? stage.label + ' recommendation' : 'Recommendation'),
        E(Select, {
          value: stageInfo.staged ? (stageRecs[stage.id] || '') : recommendation,
          label: stageInfo.staged ? stage.label + ' recommendation' : 'Recommendation',
          onChange:function(e){
            if (stageInfo.staged) setStageRec(stage.id, e.target.value);
            // The headline field keeps the latest choice so decisions, exports
            // and older screens still have a single value to read.
            setRecommendation(e.target.value);
          }, disabled:!reviewOpen},
          E(MenuItem, {value:'strong_accept'}, 'Strong accept'),
          E(MenuItem, {value:'accept'}, 'Accept'),
          E(MenuItem, {value:'neutral'}, 'Neutral'),
          E(MenuItem, {value:'reject'}, 'Reject'),
          E(MenuItem, {value:'strong_reject'}, 'Strong reject')))),

    );

  // The action bar is its own surface below the scoring card, so the buttons sit
  // on a clean background instead of floating over scrolling content.
  var actionBar = reviewOpen ? E(Paper, {elevation:0,
    sx:{mt:1.5, p:1.25, flexShrink:0, display:'flex', gap:1, alignItems:'center',
        border:1, borderColor:'divider', borderRadius:1, bgcolor:'background.paper'}},
    E(Button, {size:'medium', variant:'outlined', startIcon:E(Save),
      onClick:function(){ persistDraft(); }}, 'Save'),
    (stageInfo.staged && !isLastStage)
      ? E(Button, {fullWidth:true, size:'medium', variant:'contained', endIcon:E(NavigateNext),
          onClick:function(){ setStageIdx(activeIdx + 1); }},
          'Next: ' + stages[activeIdx + 1].label)
      : E(Tooltip, {title: allScored ? '' : 'Score every criterion first'},
          E('span', {style:{flex:1}},
            E(Button, {fullWidth:true, size:'medium', variant:'contained', startIcon:E(Send),
              onClick:submitReview,
                disabled: submitting || !allScored ||
                  (stageInfo.staged ? !stageRecs[stage.id] : !recommendation)},
              isCompleted ? 'Update review' : 'Submit review')))) : null;

  var scoringPanel = E(Box, {sx:{height: isMobile ? 'auto' : '100%', display:'flex', flexDirection:'column', minHeight:0}},
    scoringBody, actionBar);

  var stageTabs = stageInfo.staged ? E(Tabs, {value:activeIdx, onChange:function(_, v){ setStageIdx(v); },
    variant:'scrollable', scrollButtons:'auto',
    sx:{mt:1, borderBottom:1, borderColor:'divider', minHeight:36}},
    stages.map(function(s, k) {
      var done = progressFor(s.criteria);
      var label = s.label + (s.criteria.length ? ' (' + done + '/' + s.criteria.length + ')' : '')
        + (stageRecs[s.id] ? '' : ' \u2022');
      return E(Tab, {key:s.id, value:k, label:label, sx:{minHeight:36, py:0.5}});
    })) : null;

  var header = E(Box, {sx:{flexShrink:0, mb:1.5}},
    E(Box, {sx:{display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:1}},
      E(Button, {size:'small', startIcon:E(ArrowBack), onClick:function(){ navigate('/reviewer'); }}, 'All my reviews'),
      E(Box, {sx:{display:'flex', gap:0.5, alignItems:'center'}},
        idx >= 0 ? E(Typography, {variant:'caption', color:'text.secondary'},
          (idx + 1) + ' of ' + siblings.length) : null,
        E(Button, {size:'small', startIcon:E(NavigateBefore), disabled:!prev,
          onClick:function(){ go(prev); }}, 'Prev'),
        E(Button, {size:'small', endIcon:E(NavigateNext), disabled:!next,
          onClick:function(){ go(next); }}, 'Next'),
        nextUnreviewed ? E(Button, {size:'small', variant:'outlined', endIcon:E(SkipNext),
          onClick:function(){ go(nextUnreviewed); }}, 'Next unreviewed') : null)),
    E(Box, {sx:{display:'flex', justifyContent:'space-between', alignItems:'baseline', flexWrap:'wrap', gap:1}},
      E(Box, {sx:{display:'flex', alignItems:'baseline', gap:1, minWidth:0}},
        E(Typography, {variant:'h5', noWrap:true}, displayName),
        tierLabel ? E(Chip, {size:'small', label:tierLabel, variant:'outlined'}) : null),
      saveLabel ? E(Typography, {variant:'caption',
        color: dirty ? 'warning.main' : 'text.secondary'}, saveLabel) : null),
    E(Typography, {variant:'caption', color:'text.secondary'}, formTitle),
    isCompleted ? E(Alert, {severity:'info', sx:{mt:1, py:0}},
      reviewOpen ? 'Already submitted. You can still change it while reviewing is open.'
                 : 'This review has been submitted.') : null,
    !reviewOpen ? E(Alert, {severity:'warning', sx:{mt:1, py:0}},
      'Reviewing is closed for this form.') : null,
    stageTabs);

  if (isMobile) {
    return E(Box, null, header,
      E(Tabs, {value:mobileTab, onChange:function(_, v){ setMobileTab(v); }, variant:'fullWidth',
        sx:{mb:2, borderBottom:1, borderColor:'divider'}},
        E(Tab, {value:'application', label:'Application'}),
        E(Tab, {value:'scoring', label:'Scoring (' + scoredCount + '/' + required.length + ')'})),
      mobileTab === 'application' ? applicationPanel : scoringPanel);
  }

  // Two independent scroll regions: the page does not scroll, so reading the
  // application never moves the scoring controls.
  return E(Box, {sx:{height:'calc(100vh - 128px)', display:'flex', flexDirection:'column'}},
    header,
    E(Box, {sx:{flex:1, minHeight:0, display:'flex', gap:2}},
      E(Box, {sx:{flex:1, minWidth:0, overflowY:'auto', pr:1}}, applicationPanel),
      E(Box, {sx:{width:420, flexShrink:0, minHeight:0}}, scoringPanel)));
}
